import { bindPublicRuntimeContext } from "../services/cxRuntimeContext";
import type { CompanyBrandProfile } from "../branding/companyBrand";
import type { CXPublicSurveyResponse } from "../services/cxPublicSurveyService";
import type {
  FlowComponentConfig,
  FlowConfig,
  FlowScreenConfig,
  FlowScreenRouteConfig,
} from "./types";

type ObjectRecord = Record<string, unknown>;

type PublicSurveyPath = ObjectRecord & {
  deliveryMethod?: string;
  entryPageKey?: string;
  experience?: ObjectRecord & {
    cssTokens?: ObjectRecord;
    ratingType?: string;
  };
  id?: string;
  name?: string;
  pages?: PublicSurveyPage[];
  recovery?: ObjectRecord;
  routeContext?: ObjectRecord;
  reviewRouting?: ObjectRecord;
  templates?: ObjectRecord[];
  trigger?: ObjectRecord & {
    event?: string;
  };
};

type PublicSurveyPage = ObjectRecord & {
  components?: PublicSurveyComponent[];
  key?: string;
  name?: string;
  presentation?: string;
  routes?: ObjectRecord[];
};

type PublicSurveyComponent = ObjectRecord & {
  config?: ObjectRecord;
  content?: string;
  id?: string;
  persistent?: boolean;
  supportingText?: string;
  type?: string;
};

const defaultBrand: CompanyBrandProfile = {
  accent: "#3E50F7",
  background: "#FFFFFF",
  border: "#DFE3E8",
  buttonBackground: "#F4F6F8",
  buttonBorder: "#DFE3E8",
  buttonHoverBackground: "#DDE1FF",
  buttonSelectedBackground: "#3E50F7",
  buttonSelectedBorder: "#3E50F7",
  buttonSelectedText: "#FFFFFF",
  buttonText: "#1C252E",
  companyName: "Apruv",
  customerName: "Customer",
  domain: "svcpro.io",
  fontFamily: "Public Sans",
  initials: "A",
  primary: "#3E50F7",
  primaryContrast: "#FFFFFF",
  primaryDark: "#2336D4",
  primarySoft: "#DDE1FF",
  surface: "#FFFFFF",
  technicianName: "Technician",
  text: "#1C252E",
  textMuted: "#637381",
};

export function publicSurveyToFlow(
  response: CXPublicSurveyResponse,
): FlowConfig {
  response = bindPublicRuntimeContext(response);
  const survey = objectValue(response.customerSurvey);
  const path = selectSurveyPath(survey, response.triggerEvent);
  const pages = arrayValue<PublicSurveyPage>(path.pages);
  const tokenValues = surveyTokenValues(response, survey);
  const brandingLogoUrl =
    validPublicAssetUrl(textValue(objectValue(survey.branding).logoUrl, "")) ??
    validPublicAssetUrl(
      textValue(objectValue(survey.branding).faviconUrl, ""),
    ) ??
    undefined;
  const configuredRouteContext = Object.fromEntries(
    Object.entries(objectValue(path.routeContext)).filter(
      ([, value]) =>
        ["string", "number", "boolean"].includes(typeof value) ||
        (Array.isArray(value) &&
          value.every((item) => typeof item === "string")),
    ),
  ) as NonNullable<FlowConfig["routeContext"]>;

  return {
    brand: buildBrand(survey, path, tokenValues),
    celebrationEffect: oneOf(
      survey.celebrationEffect,
      ["none", "confetti"],
      "none",
    ),
    entryScreenId: textValue(path.entryPageKey, undefined),
    id: `cx-public-${response.moduleRevisionId || response.triggerGroupId}`,
    name: textValue(path.name, response.module?.name || "Customer survey"),
    routeContext: {
      ...configuredRouteContext,
      channel: textValue(path.deliveryMethod, "sms"),
      service_type: textValue(response.sourceEvent?.serviceType, ""),
      trigger_event: response.triggerEvent,
    },
    screens: pages.length
      ? pages.map((page, index) =>
          pageToScreen(page, path, tokenValues, index, brandingLogoUrl),
        )
      : [fallbackScreen(tokenValues)],
  };
}

function selectSurveyPath(
  survey: ObjectRecord,
  triggerEvent: string,
): PublicSurveyPath {
  const paths = arrayValue<PublicSurveyPath>(survey.paths);
  const normalizedTrigger = normalizeTriggerEvent(triggerEvent);
  return (
    paths.find(
      (path) =>
        normalizeTriggerEvent(textValue(path.trigger?.event, "")) ===
        normalizedTrigger,
    ) ??
    paths[0] ??
    {}
  );
}

function pageToScreen(
  page: PublicSurveyPage,
  path: PublicSurveyPath,
  tokenValues: Record<string, string>,
  index: number,
  brandingLogoUrl?: string,
): FlowScreenConfig {
  const components = arrayValue<PublicSurveyComponent>(page.components);
  const ratingOwnsSubmit = components.some(isRatingComponent);
  const feedbackComponents = components.filter((component) =>
    ["comment_box", "tag_selector"].includes(textValue(component.type, "")),
  );
  const feedbackSubmitComponentId = !ratingOwnsSubmit
    ? feedbackComponents[feedbackComponents.length - 1]?.id
    : undefined;
  const buttonOwnsAction = components.some((component) =>
    ["primary_button", "secondary_button"].includes(
      textValue(component.type, ""),
    ),
  );
  const finixTipOwnsAction = components.some(
    (component) => component.type === "finix_tip_section",
  );
  const stripeTipOwnsAction = components.some(
    (component) => component.type === "tip_section",
  );
  const routes = routesForPage(page, path);
  const defaultButtonTargetScreenId =
    routes?.length === 1 && routes[0].logic.mode === "always"
      ? routes[0].targetScreenId
      : undefined;
  const terminalPresentationOwnsAction =
    !routes?.length &&
    components.some((component) =>
      ["review_destinations", "visual_asset"].includes(
        textValue(component.type, ""),
      ),
    );

  return {
    backLabel: "Back",
    components: components
      .filter(
        (component) =>
          componentIsVisible(component, path, tokenValues) &&
          !(
            (ratingOwnsSubmit || feedbackSubmitComponentId) &&
            component.type === "primary_button"
          ) &&
          !(
            stripeTipOwnsAction &&
            ["primary_button", "secondary_button"].includes(
              textValue(component.type, ""),
            )
          ),
      )
      .map((component) => {
        const mapped = componentToFlow(component, path, tokenValues, {
          defaultTargetScreenId: defaultButtonTargetScreenId,
          feedbackSubmitComponentId,
          brandingLogoUrl,
          pageKey: textValue(page.key, `screen-${index + 1}`),
          relatedFeedbackComponentIds: feedbackComponents
            .filter(
              (feedbackComponent) => feedbackComponent.id !== component.id,
            )
            .map((feedbackComponent) => textValue(feedbackComponent.id, ""))
            .filter(Boolean),
        });
        if (!mapped) return null;
        const persistent = booleanValue(
          component.persistent,
          booleanValue(objectValue(component.config).persistent, false),
        );
        return {
          ...mapped,
          ...(persistent ? { persistent: true } : {}),
        } as FlowComponentConfig;
      })
      .filter(Boolean) as FlowComponentConfig[],
    hideFooterAction:
      ratingOwnsSubmit ||
      Boolean(feedbackSubmitComponentId) ||
      buttonOwnsAction ||
      finixTipOwnsAction ||
      stripeTipOwnsAction ||
      terminalPresentationOwnsAction,
    id: textValue(page.key, `screen-${index + 1}`),
    nextLabel: nextLabelForPage(page),
    presentation: oneOf(
      page.presentation,
      ["default", "confirmation_card", "recovery_review"],
      "default",
    ),
    previewLabel: textValue(page.name, undefined),
    routes,
    showBack: index > 0,
    statusLabel: `${index + 1}/${arrayValue(path.pages).length || 1}`,
  };
}

function componentToFlow(
  component: PublicSurveyComponent,
  path: PublicSurveyPath,
  tokenValues: Record<string, string>,
  options: {
    brandingLogoUrl?: string;
    defaultTargetScreenId?: string;
    feedbackSubmitComponentId?: string;
    pageKey: string;
    relatedFeedbackComponentIds: string[];
  },
): FlowComponentConfig | null {
  const id = textValue(
    component.id,
    `${component.type || "component"}-${Math.random().toString(36).slice(2)}`,
  );
  const config = objectValue(component.config);
  const content = resolveTokens(textValue(component.content, ""), tokenValues);
  const supportingText = resolveTokens(
    textValue(component.supportingText, ""),
    tokenValues,
  );

  switch (component.type) {
    case "top_bar": {
      const logoMode = textValue(config.logoMode, "");
      const componentLogoUrl = validPublicAssetUrl(
        textValue(config.logoUrl, ""),
      );
      return {
        align: oneOf(config.align, ["left", "center", "right"], "left"),
        id,
        logoFont: textValue(config.logoFont, "") || undefined,
        logoLabel:
          stripHtml(content) || tokenValues["organization.name"] || undefined,
        logoUrl:
          logoMode === "text"
            ? undefined
            : logoMode === "organization" || logoMode === "upload"
              ? (componentLogoUrl ?? undefined)
              : (options.brandingLogoUrl ?? componentLogoUrl ?? undefined),
        phoneLabel: tokenValues["organization.phone"] || undefined,
        size: oneOf(config.size, ["compact", "standard", "hero"], "compact"),
        style: oneOf(config.topBarStyle, ["minimal", "card"], "minimal"),
        type: "topBar",
      };
    }
    case "back_button": {
      const configuredTarget = textValue(config.routeTargetPageKey, "");
      return {
        id,
        label: stripHtml(content) || "Back",
        targetScreenId: pageKeyExists(path, configuredTarget)
          ? configuredTarget
          : undefined,
        type: "backButton",
      };
    }
    case "header":
      return textComponent(id, "h1", content, config);
    case "subheader":
    case "body":
      return textComponent(id, "subheader", content, config);
    case "proof_media": {
      const sourceMode = textValue(config.sourceMode, "auto");
      const managedVideo = Object.prototype.hasOwnProperty.call(config, 'videoAsset');
      const configuredMedia = arrayValue<ObjectRecord>(config.items).map(
        (item, itemIndex) => ({
          label: managedVideo
            ? stripHtml(resolveTokens(textValue(item.label, ''), tokenValues)) || 'Welcome video'
            : textValue(item.label, `Service media ${itemIndex + 1}`),
          notes: managedVideo
            ? resolveTokens(textValue(item.notes, ''), tokenValues) || undefined
            : textValue(item.notes, undefined),
          thumbnailUrl:
            validHttpUrl(textValue(item.thumbnailUrl, "")) || undefined,
          type: oneOf(item.type, ["image", "video", "voice", "note"], "image"),
          url: validHttpUrl(textValue(item.url, "")) || undefined,
        }),
      );
      if (sourceMode === "runtime" && configuredMedia.length === 0) return null;
      const showBrandingFallback =
        sourceMode === "branding" || configuredMedia.length === 0;
      return {
        id,
        items: configuredMedia.length
          ? configuredMedia
          : [
              {
                label: showBrandingFallback
                  ? tokenValues["organization.name"] || "Organization"
                  : "Service photo",
                notes: supportingText || undefined,
                thumbnailUrl: showBrandingFallback
                  ? options.brandingLogoUrl
                  : undefined,
                type: "image",
                url: showBrandingFallback ? options.brandingLogoUrl : undefined,
              },
            ],
        showTabs: booleanValue(config.showTabs, !showBrandingFallback),
        title: content || "Proof of service",
        type: "proofOfServiceMedia",
      };
    }
    case "technician_notes":
      if (!supportingText) return null;
      return {
        body: supportingText,
        id,
        title: content || "Technician notes",
        type: "notes",
      };
    case "rating_scale":
    case "csat_rating":
    case "nps_rating":
      if (ratingTypeForComponent(component, path) === "nps") {
        return {
          actionLabel: textValue(config.actionLabel, "Submit and continue"),
          allowResubmit: booleanValue(config.allowResubmit, true),
          feedbackMode: oneOf(
            config.feedbackMode,
            ["none", "inline", "follow_up"],
            "follow_up",
          ),
          feedbackPlaceholder: textValue(
            config.feedbackPlaceholder,
            "Tell us what stood out or what we should fix.",
          ),
          feedbackPrompt: textValue(
            config.feedbackPrompt,
            "Add optional context",
          ),
          feedbackTags: stringArray(config.feedbackTags, [
            "Speed",
            "Quality",
            "Communication",
            "Technician",
            "Price",
          ]),
          editResponseLabel: textValue(
            config.editResponseLabel,
            "Edit response",
          ),
          highLabel: textValue(config.highLabel, "Very likely"),
          id,
          lowLabel: textValue(config.lowLabel, "Not likely"),
          colorCoding: booleanValue(config.npsColorCoding, false),
          detractorColor: textValue(config.npsDetractorColor, "#FECACA"),
          detractorMax: numberValue(config.npsDetractorMax, 6),
          layout: oneOf(
            config.npsLayout,
            [
              "split",
              "single_row",
              "grid_4_4_3",
              "grid_3_4_4",
              "grouped_bands",
              "vertical",
            ],
            "split",
          ),
          neutralColor: textValue(config.npsNeutralColor, "#FEF3C7"),
          neutralMax: numberValue(config.npsNeutralMax, 8),
          promoterColor: textValue(config.npsPromoterColor, "#DCFCE7"),
          style: oneOf(
            config.npsStyle ?? config.npsLayout,
            ["standard", "pills", "circles", "tiles", "segments"],
            "standard",
          ),
          question: content || "How likely are you to recommend us?",
          submittedActionLabel: textValue(
            config.submittedActionLabel,
            "Continue",
          ),
          submittedBody: textValue(
            config.submittedBody,
            "You can update your answer or continue to the next step.",
          ),
          submittedTitle: textValue(
            config.submittedTitle,
            "Feedback already submitted",
          ),
          type: "npsForm",
        };
      }
      return {
        actionLabel: textValue(config.actionLabel, "Submit and continue"),
        allowResubmit: booleanValue(config.allowResubmit, true),
        feedbackMode: oneOf(
          config.feedbackMode,
          ["none", "inline", "follow_up"],
          "follow_up",
        ),
        feedbackPlaceholder: textValue(
          config.feedbackPlaceholder,
          "Tell us what stood out or what we should fix.",
        ),
        feedbackPrompt: textValue(
          config.feedbackPrompt,
          "Add optional context",
        ),
        feedbackTags: stringArray(config.feedbackTags, [
          "Speed",
          "Quality",
          "Communication",
          "Technician",
          "Price",
        ]),
        colorCoding: booleanValue(config.csatColorCoding, false),
        editResponseLabel: textValue(config.editResponseLabel, "Edit response"),
        id,
        layout: oneOf(config.csatLayout, ["row", "grid", "vertical"], "row"),
        negativeColor: textValue(config.csatNegativeColor, "#FECACA"),
        neutralColor: textValue(config.csatNeutralColor, "#FEF3C7"),
        options: stringArray(config.options, [
          "Bad",
          "Okay",
          "Good",
          "Great",
        ]).map((label, index) => ({ label, value: index + 1 })),
        positiveColor: textValue(config.csatPositiveColor, "#DCFCE7"),
        question: content || "Rate your experience",
        style: oneOf(
          config.csatStyle,
          ["faces_labels", "faces", "labels", "compact"],
          "faces_labels",
        ),
        submittedActionLabel: textValue(
          config.submittedActionLabel,
          "Continue",
        ),
        submittedBody: textValue(
          config.submittedBody,
          "You can update your answer or continue to the next step.",
        ),
        submittedTitle: textValue(
          config.submittedTitle,
          "Feedback already submitted",
        ),
        type: "csatForm",
      };
    case "comment_box":
    case "tag_selector":
      return {
        actionLabel: textValue(config.actionLabel, "Submit and continue"),
        align: oneOf(config.align, ["left", "center"], "left"),
        allowResubmit: booleanValue(config.allowResubmit, true),
        attachMode:
          textValue(config.attachToRatingMode, "") === "none"
            ? "none"
            : textValue(config.attachToRatingMode, "") === "specific_rating"
              ? "specificRating"
              : "previousRating",
        attachToComponentId: textValue(config.attachToComponentId, undefined),
        id,
        inputMode: component.type === "comment_box" ? "text" : "tags",
        maxWidth: numberValue(config.maxWidth, undefined),
        placeholder:
          component.type === "comment_box"
            ? content || "Say something here..."
            : undefined,
        relatedFeedbackComponentIds: options.relatedFeedbackComponentIds,
        showSubmitAction: component.id === options.feedbackSubmitComponentId,
        submittedActionLabel: textValue(
          config.submittedActionLabel,
          "Continue",
        ),
        submittedBody: textValue(
          config.submittedBody,
          "You can update your response or continue to the next step.",
        ),
        submittedTitle: textValue(
          config.submittedTitle,
          "Feedback already submitted",
        ),
        tags:
          component.type === "tag_selector"
            ? stringArray(config.options, [])
            : undefined,
        title:
          component.type === "comment_box"
            ? supportingText || undefined
            : content || undefined,
        style: oneOf(config.feedbackStyle, ["card", "plain"], "card"),
        type: "feedbackForm",
      };
    case "review_destinations": {
      const variant = oneOf(
        config.variant,
        ["standard", "no_nps_v3"],
        "standard",
      );
      const resolvedTitle =
        resolveTokens(textValue(content, ""), tokenValues) || undefined;
      // No-rating (no_nps_v3): recognition/bonus copy is page header/subheader
      // (Figma). Do not invent a review-block title like "Choose where to post".
      const title =
        variant === "no_nps_v3"
          ? resolvedTitle && resolvedTitle !== "Choose where to post"
            ? resolvedTitle
            : undefined
          : resolvedTitle || "Choose where to post";
      return {
        bonusAmount: numberValue(config.bonusAmount, undefined),
        bonusMessage:
          resolveTokens(textValue(config.bonusMessage, ""), tokenValues) ||
          undefined,
        buttonBorderRadius: numberValue(config.buttonBorderRadius, undefined),
        buttonMinHeight: numberValue(config.buttonMinHeight, undefined),
        buttonStyle: oneOf(
          config.buttonStyle,
          ["filled", "outlined"],
          "filled",
        ),
        callbackLabel: textValue(config.callbackLabel, undefined),
        callbackPhone: tokenValues["organization.phone"] || undefined,
        id,
        reviewButtonLabel:
          resolveTokens(textValue(config.reviewButtonLabel, ""), tokenValues) ||
          undefined,
        reviewPromptBody:
          resolveTokens(textValue(config.reviewPromptBody, ""), tokenValues) ||
          undefined,
        showFeedbackPreview: booleanValue(config.showFeedbackPreview, true),
        sites: reviewSites(path),
        technicianName: tokenValues["fieldrep.first_name"] || undefined,
        title,
        type: "reviewSelection",
        variant,
      };
    }
    case "tip_section": {
      const configuredTipAmounts = arrayValue<unknown>(config.tipAmounts)
        .map((amount) => numberValue(amount, 0))
        .filter((amount) => amount > 0)
        .slice(0, 6);
      const tipAmounts = configuredTipAmounts.length
        ? configuredTipAmounts
        : [5, 10, 15];

      return {
        allowCustomAmount: booleanValue(config.tipAllowCustomAmount, true),
        amounts: tipAmounts.map((amount) => ({
          label: `$${amount}`,
          value: amount,
        })),
        buttonBackgroundColor: textValue(
          config.tipButtonBackgroundColor,
          undefined,
        ),
        buttonBorderColor: textValue(config.tipButtonBorderColor, undefined),
        buttonTextColor: textValue(config.tipButtonTextColor, undefined),
        checkoutEnabled: true,
        continueLabel: "Continue",
        id,
        layout: oneOf(config.tipLayout, ["row", "vertical"], "row"),
        selectedBackgroundColor: textValue(
          config.tipSelectedBackgroundColor,
          undefined,
        ),
        selectedBorderColor: textValue(
          config.tipSelectedBorderColor,
          undefined,
        ),
        selectedTextColor: textValue(config.tipSelectedTextColor, undefined),
        skipLabel: "Skip tipping",
        style: oneOf(config.tipStyle, ["tiles", "pills", "compact"], "tiles"),
        termsEnabled: booleanValue(config.termsEnabled, false),
        termsHtml: textValue(config.termsHtml, undefined),
        termsLinkLabel: textValue(config.termsLinkLabel, undefined),
        termsModalTitle: textValue(config.termsModalTitle, undefined),
        termsText: textValue(config.termsText, undefined),
        type: "tipping",
      };
    }
    case "finix_tip_section":
      return {
        termsEnabled: booleanValue(config.termsEnabled, true),
        termsHtml: textValue(config.termsHtml, undefined),
        termsText: textValue(config.termsText, undefined),
        termsLinkLabel: textValue(config.termsLinkLabel, undefined),
        termsModalTitle: textValue(config.termsModalTitle, undefined),
        allowCustomAmount: true,
        amounts: [
          { label: "$5", value: 5 },
          { label: "$10", value: 10 },
          { label: "$15", value: 15 },
        ],
        id,
        noThanksLabel: "No thanks",
        paymentTitle: "Payment Method",
        subtitle: supportingText || undefined,
        tipAmountLabel: content || "Tip to make your technician's day",
        type: "finixTipping",
      };
    case "primary_button":
    case "secondary_button": {
      const configuredTarget = textValue(config.routeTargetPageKey, "");
      return {
        action: buttonAction(config),
        ...(config.size === "hero" ? { fontSize: 18 } : {}),
        description: supportingText || undefined,
        id,
        label: content || "Continue",
        routeValue: textValue(config.routeValue, undefined),
        routeValueKey: textValue(config.routeValueKey, undefined),
        showArrow: false,
        style: oneOf(
          config.buttonStyle,
          ["filled", "outlined", "text"],
          component.type === "secondary_button" ? "outlined" : "filled",
        ),
        targetScreenId:
          (pageKeyExists(path, configuredTarget) ? configuredTarget : "") ||
          options.defaultTargetScreenId ||
          defaultRouteTarget(path, options.pageKey) ||
          undefined,
        textAlign: "center",
        type: "actionButton",
      };
    }
    case "callback_cta":
      return {
        action: "callback",
        description:
          supportingText || "Request a recovery callback from the team.",
        id,
        label:
          content ||
          textValue(path.recovery?.callbackLabel, "Request a callback"),
        phone: tokenValues["organization.phone"] || undefined,
        showArrow: false,
        style: oneOf(
          config.buttonStyle,
          ["filled", "outlined", "text"],
          "outlined",
        ),
        textAlign: "center",
        type: "actionButton",
      };
    case "referral_cta":
      return {
        id,
        options: [
          {
            description:
              supportingText || "Share a referral link with a neighbor.",
            id: "apruv_native",
            label: content || "Refer a friend",
          },
        ],
        title: "Referral",
        type: "referralSelection",
      };
    case "thank_you":
      return {
        align: "center",
        id,
        text: content || "Thank you",
        type: "h2",
      };
    case "visual_asset":
      return {
        alt: textValue(config.alt, undefined),
        animation:
          config.animation === "gift_box_thank_you"
            ? "gift_box_thank_you"
            : undefined,
        id,
        maxWidth: numberValue(config.maxWidth, 315),
        preset:
          config.preset === "negative_feedback" ||
          config.preset === "recovery_contact" ||
          config.preset === "success_check"
            ? config.preset
            : undefined,
        type: "visualAsset",
        url: textValue(config.url, undefined),
      };
    case "video": {
      const configuredVideoAsset = objectValue(config.videoAsset);
      const configuredAsset = Object.keys(configuredVideoAsset).length
        ? configuredVideoAsset
        : objectValue(config.asset);
      const asset = {
        contentType: textValue(configuredAsset.contentType, undefined),
        id: textValue(configuredAsset.id, undefined),
        originalFilename: textValue(
          configuredAsset.originalFilename,
          undefined,
        ),
        playbackUrl:
          validHttpUrl(textValue(configuredAsset.playbackUrl, "")) ?? undefined,
        status: textValue(configuredAsset.status, undefined),
      };
      const hasAssetMetadata = Object.values(asset).some(Boolean);

      return {
        asset: hasAssetMetadata ? asset : undefined,
        caption:
          resolveTokens(
            textValue(config.caption, supportingText),
            tokenValues,
          ) || undefined,
        id,
        posterUrl:
          validPublicAssetUrl(textValue(config.posterUrl, "")) ?? undefined,
        title:
          resolveTokens(textValue(config.title, content), tokenValues) ||
          undefined,
        type: "videoPlayer",
      };
    }
    case "spacer":
      return {
        id,
        size: oneOf(config.size, ["compact", "standard", "hero"], "standard"),
        type: "spacer",
      };
    default:
      return null;
  }
}

function textComponent(
  id: string,
  type: "h1" | "subheader",
  text: string,
  config: ObjectRecord,
): FlowComponentConfig {
  return {
    align: oneOf(config.align, ["left", "center", "right"], "left"),
    id,
    size: oneOf(config.size, ["compact", "standard", "hero"], "standard"),
    text,
    type,
  };
}

function routesForPage(
  page: PublicSurveyPage,
  path: PublicSurveyPath,
): FlowScreenRouteConfig[] | undefined {
  const configuredRoutes = arrayValue<ObjectRecord>(page.routes).reduce<
    FlowScreenRouteConfig[]
  >((routes, route) => {
    const logic = objectValue(route.logic);
    const targetScreenId = textValue(route.targetScreenId, "");
    if (!targetScreenId) return routes;

    routes.push({
      label: textValue(route.label, undefined),
      logic: {
        expression: textValue(logic.expression, ""),
        field: oneOf(
          logic.field,
          [
            "channel",
            "comment",
            "custom",
            "has_existing_response",
            "rating_enabled",
            "response_editing_enabled",
            "score",
            "score_method",
            "selected_option",
            "sentiment",
            "service_type",
            "tag",
            "tipping_enabled",
          ],
          "custom",
        ),
        mode: oneOf(logic.mode, ["always", "fallback", "rule"], "always"),
        operator: oneOf(
          logic.operator,
          [
            "contains",
            "equals",
            "exists",
            "greater_than",
            "greater_than_or_equal",
            "less_than",
            "not_equals",
          ],
          "equals",
        ),
        value: textValue(logic.value, ""),
      },
      targetScreenId,
    });
    return routes;
  }, []);
  if (configuredRoutes.length) return configuredRoutes;

  const pageKey = textValue(page.key, "");
  const ratingComponent = arrayValue<PublicSurveyComponent>(
    page.components,
  ).find(isRatingComponent);
  if (!ratingComponent) return Array.isArray(page.routes) ? [] : undefined;

  const routes: FlowScreenRouteConfig[] = [];
  const positiveTarget = firstExistingPage(path, [
    "tipping",
    "positive",
    "completion",
  ]);
  const neutralTarget = firstExistingPage(path, ["neutral", "completion"]);
  const negativeTarget = firstExistingPage(path, [
    "negative",
    "recovery_completion",
    "completion",
  ]);

  if (positiveTarget && positiveTarget !== pageKey) {
    routes.push({
      label: "High score",
      logic: {
        expression: "",
        field: "score",
        mode: "rule",
        operator: "greater_than_or_equal",
        value:
          textValue(path.experience?.ratingType, "csat") === "nps"
            ? "9"
            : String(numberValue(path.reviewRouting?.positiveThreshold, 4)),
      },
      targetScreenId: positiveTarget,
    });
  }
  if (neutralTarget && neutralTarget !== pageKey) {
    routes.push({
      label: "Mid score",
      logic: {
        expression: "",
        field: "score",
        mode: "rule",
        operator:
          textValue(path.experience?.ratingType, "csat") === "nps"
            ? "greater_than_or_equal"
            : "equals",
        value:
          textValue(path.experience?.ratingType, "csat") === "nps" ? "7" : "3",
      },
      targetScreenId: neutralTarget,
    });
  }
  if (negativeTarget && negativeTarget !== pageKey) {
    routes.push({
      label: "Low score",
      logic: {
        expression: "",
        field: "score",
        mode: "rule",
        operator: "less_than",
        value:
          textValue(path.experience?.ratingType, "csat") === "nps"
            ? "7"
            : String(numberValue(path.recovery?.negativeThreshold, 3)),
      },
      targetScreenId: negativeTarget,
    });
  }

  return routes.length ? routes : undefined;
}

function firstExistingPage(path: PublicSurveyPath, pageKeys: string[]) {
  const existing = new Set(
    arrayValue<PublicSurveyPage>(path.pages).map((page) =>
      textValue(page.key, ""),
    ),
  );
  return pageKeys.find((pageKey) => existing.has(pageKey)) ?? null;
}

function pageKeyExists(path: PublicSurveyPath, pageKey: string) {
  return Boolean(pageKey) && firstExistingPage(path, [pageKey]) === pageKey;
}

function defaultRouteTarget(path: PublicSurveyPath, pageKey: string) {
  switch (pageKey) {
    case "landing":
      return firstExistingPage(path, ["tipping", "positive", "completion"]);
    case "tipping":
    case "positive":
    case "neutral":
      return firstExistingPage(path, ["completion"]);
    case "negative":
      return firstExistingPage(path, ["recovery_completion", "completion"]);
    default:
      return null;
  }
}

function isRatingComponent(component: PublicSurveyComponent) {
  return ["rating_scale", "csat_rating", "nps_rating"].includes(
    textValue(component.type, ""),
  );
}

function ratingTypeForComponent(
  component: PublicSurveyComponent,
  path: PublicSurveyPath,
) {
  if (component.type === "nps_rating") return "nps";
  if (component.type === "csat_rating") return "csat";
  return textValue(path.experience?.ratingType, "csat");
}

function reviewSites(
  path: PublicSurveyPath,
): Extract<FlowComponentConfig, { type: "reviewSelection" }>["sites"] {
  return arrayValue<ObjectRecord>(objectValue(path.reviewRouting).allocations)
    .map((allocation, index) => {
      const reviewUrl = validHttpUrl(
        textValue(allocation.overrideReviewUrl, ""),
      );
      if (!reviewUrl) return null;

      return {
        description:
          textValue(allocation.overrideLocationLabel, "") || undefined,
        destinationSource: oneOf(
          allocation.destinationSource,
          ["integration", "override"],
          "override",
        ),
        id: textValue(allocation.id, `review-site-${index + 1}`),
        label: reviewProviderLabel(textValue(allocation.provider, "google")),
        locationLabel:
          textValue(allocation.integrationMappingLabel, "") ||
          textValue(allocation.overrideLocationLabel, "") ||
          undefined,
        provider: oneOf(
          allocation.provider,
          ["google", "yelp", "facebook", "bbb"],
          "google",
        ),
        strategy: oneOf(
          objectValue(path.reviewRouting).strategy,
          ["show_all", "weighted", "sequential"],
          "show_all",
        ),
        url: reviewUrl,
        weight: numberValue(allocation.weight, undefined),
      };
    })
    .filter((site): site is NonNullable<typeof site> => Boolean(site));
}

function buildBrand(
  survey: ObjectRecord,
  path: PublicSurveyPath,
  tokenValues: Record<string, string>,
): CompanyBrandProfile {
  const branding = objectValue(survey.branding);
  const cssTokens = objectValue(path.experience?.cssTokens);
  const companyName = textValue(
    branding.brandName,
    tokenValues["organization.name"] || defaultBrand.companyName,
  );
  const initials = companyName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return {
    ...defaultBrand,
    accent: textValue(cssTokens.accent, defaultBrand.accent),
    background: textValue(cssTokens.background, defaultBrand.background),
    border: textValue(cssTokens.border, defaultBrand.border),
    buttonBackground: textValue(
      cssTokens.buttonBackground,
      defaultBrand.buttonBackground,
    ),
    buttonBorder: textValue(cssTokens.buttonBorder, defaultBrand.buttonBorder),
    buttonHoverBackground: textValue(
      cssTokens.buttonHoverBackground,
      defaultBrand.buttonHoverBackground,
    ),
    buttonSelectedBackground: textValue(
      cssTokens.buttonSelectedBackground,
      defaultBrand.buttonSelectedBackground,
    ),
    buttonSelectedBorder: textValue(
      cssTokens.buttonSelectedBorder,
      defaultBrand.buttonSelectedBorder,
    ),
    buttonSelectedText: textValue(
      cssTokens.buttonSelectedText,
      defaultBrand.buttonSelectedText,
    ),
    buttonText: textValue(cssTokens.buttonText, defaultBrand.buttonText),
    companyName,
    customerName:
      [tokenValues["customer.first_name"], tokenValues["customer.last_name"]]
        .filter(Boolean)
        .join(" ") || defaultBrand.customerName,
    domain: textValue(
      branding.domain,
      window.location.host || defaultBrand.domain,
    ),
    faviconUrl:
      validPublicAssetUrl(textValue(branding.faviconUrl, "")) ?? undefined,
    fontFamily: oneOf(
      branding.fontFamily,
      ["Inter", "Public Sans", "DM Sans", "Nunito Sans", "Barlow"],
      defaultBrand.fontFamily,
    ),
    initials: initials || defaultBrand.initials,
    primary: textValue(cssTokens.primary, defaultBrand.primary),
    primaryContrast: textValue(
      cssTokens.primaryContrast,
      defaultBrand.primaryContrast,
    ),
    primaryDark: textValue(cssTokens.primaryDark, defaultBrand.primaryDark),
    primarySoft: textValue(cssTokens.primarySoft, defaultBrand.primarySoft),
    surface: textValue(cssTokens.surface, defaultBrand.surface),
    technicianName:
      tokenValues["fieldrep.first_name"] || defaultBrand.technicianName,
    text: textValue(cssTokens.text, defaultBrand.text),
    textMuted: textValue(cssTokens.textMuted, defaultBrand.textMuted),
  };
}

function surveyTokenValues(
  response: CXPublicSurveyResponse,
  survey: ObjectRecord,
): Record<string, string> {
  const raw = objectValue(objectValue(response.sourceEvent?.payload).raw);
  const branding = objectValue(survey.branding);
  const firstName = textValue(
    raw.customerFirstName,
    textValue(raw.firstName, ""),
  );
  const lastName = textValue(raw.customerLastName, textValue(raw.lastName, ""));
  const technicianFirstName = textValue(
    raw.technicianFirstName,
    textValue(raw.fieldrepFirstName, ""),
  );
  const organizationName = textValue(
    raw.companyName,
    textValue(branding.brandName, textValue(raw.organizationName, "")),
  );

  const values: Record<string, string> = {
    "appointment.id": response.appointmentCode,
    "appointment.service_type": textValue(
      response.sourceEvent?.serviceType,
      textValue(raw.serviceType, ""),
    ),
    "appointment.technician_notes": textValue(
      raw.technicianNotes,
      textValue(raw.notes, ""),
    ),
    "company.name": organizationName,
    "contact.first_name": firstName,
    "contact.last_name": lastName,
    "contact.name": [firstName, lastName].filter(Boolean).join(" "),
    "customer.first_name": firstName,
    "customer.last_name": lastName,
    "customer.name": [firstName, lastName].filter(Boolean).join(" "),
    "fieldrep.first_name":
      technicianFirstName || textValue(raw.technicianName, ""),
    "location.name": textValue(raw.locationName, ""),
    "organization.name": organizationName,
    "organization.phone": textValue(raw.organizationPhone, ""),
  };

  return values;
}

function componentIsVisible(
  component: PublicSurveyComponent,
  path: PublicSurveyPath,
  tokenValues: Record<string, string>,
) {
  const visibility = textValue(
    objectValue(component.config).visibility,
    "always",
  );
  if (visibility === "has_technician_notes") {
    return Boolean(tokenValues["appointment.technician_notes"]);
  }

  const routeContext = objectValue(path.routeContext);
  const hasServiceProof = booleanValue(
    routeContext.has_service_proof,
    pathContainsServiceProof(path),
  );
  if (visibility === "has_service_proof") return hasServiceProof;
  if (visibility === "missing_service_proof") return !hasServiceProof;
  return true;
}

function pathContainsServiceProof(path: PublicSurveyPath) {
  return arrayValue<PublicSurveyPage>(path.pages).some((page) =>
    arrayValue<PublicSurveyComponent>(page.components).some((component) => {
      if (component.type !== "proof_media") return false;
      const config = objectValue(component.config);
      return (
        textValue(config.sourceMode, "auto") === "service_proof" &&
        arrayValue(config.items).length > 0
      );
    }),
  );
}

function fallbackScreen(tokenValues: Record<string, string>): FlowScreenConfig {
  return {
    components: [
      {
        id: "fallback-heading",
        text: `Thanks${tokenValues["customer.first_name"] ? `, ${tokenValues["customer.first_name"]}` : ""}`,
        type: "h1",
      },
      {
        id: "fallback-subheader",
        text: "Your survey is ready.",
        type: "subheader",
      },
    ],
    id: "landing",
    nextLabel: "Continue",
  };
}

function nextLabelForPage(page: PublicSurveyPage) {
  const button = arrayValue<PublicSurveyComponent>(page.components).find(
    (component) =>
      ["primary_button", "secondary_button"].includes(
        textValue(component.type, ""),
      ),
  );
  if (!button) return undefined;
  return stripHtml(textValue(button?.content, "Continue")) || "Continue";
}

function buttonAction(config: ObjectRecord) {
  const action = textValue(config.buttonAction, "continue");
  if (action === "edit_response") return "edit_response";
  if (action === "public_review") return "review";
  if (
    action === "tip" ||
    action === "referral" ||
    action === "callback" ||
    action === "contact"
  )
    return action;
  return "continue";
}

function reviewProviderLabel(provider: string) {
  switch (provider) {
    case "bbb":
      return "BBB";
    case "facebook":
      return "Facebook";
    case "yelp":
      return "Yelp";
    default:
      return "Google";
  }
}

function normalizeTriggerEvent(value: string) {
  if (value === "appointment_completed") return "completed_appointment";
  return value || "completed_appointment";
}

function resolveTokens(value: string, tokenValues: Record<string, string>) {
  const genericFallbacks: Record<string, string> = {
    "company.name": "your service provider",
    "fieldrep.first_name": "your technician",
    "organization.name": "your service provider",
    tech_name: "your technician",
    "technician.first_name": "your technician",
  };

  return value
    .replace(
      /{{\s*([a-zA-Z0-9_.-]+)\s*}}/g,
      (_token, key) => tokenValues[key] || genericFallbacks[key] || "",
    )
    .replace(/\s+([,.;!?])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, "").trim();
}

function objectValue(value: unknown): ObjectRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as ObjectRecord)
    : {};
}

function arrayValue<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function textValue(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function validHttpUrl(value: string): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
}

function validPublicAssetUrl(value: string): string | null {
  const validUrl = validHttpUrl(value);
  if (!validUrl) return null;

  const hostname = new URL(validUrl).hostname.toLowerCase();
  if (
    hostname === "via.placeholder.com" ||
    hostname === "placeholder.com" ||
    hostname === "placehold.co" ||
    hostname === "dummyimage.com"
  ) {
    return null;
  }
  return validUrl;
}

function numberValue(
  value: unknown,
  fallback: number | undefined,
): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function booleanValue(value: unknown, fallback: boolean) {
  return typeof value === "boolean" ? value : fallback;
}

function stringArray(value: unknown, fallback: string[]) {
  if (!Array.isArray(value)) return fallback;
  return value.filter((item): item is string => typeof item === "string");
}

function oneOf<T extends string>(
  value: unknown,
  options: readonly T[],
  fallback: T,
): T {
  return typeof value === "string" && options.includes(value as T)
    ? (value as T)
    : fallback;
}

/** Low-level presentation primitives shared with canonical rendering; no route inference. */
export const surveyPresentationPrimitives = { componentToFlow, buildBrand, surveyTokenValues, resolveTokens };

/** Shared template substitution for canonical presentation labels; invocation-owned values stay authoritative. */
export function resolveSurveyTemplate(value: string, response: CXPublicSurveyResponse, branding: ObjectRecord, fallbacks: Record<string, string> = {}) {
  const values = surveyTokenValues(response, { branding });
  values['appointment.technician_notes'] = response.runtimeContext?.technicianNotes ?? '';
  values['organization.phone'] = response.runtimeContext?.contactPhone ?? '';
  return resolveTokens(value, { ...fallbacks, ...Object.fromEntries(Object.entries(values).filter(([, token]) => token !== '')) });
}
